import { useState } from 'react';
import { useGetLocale, useTranslate } from '@refinedev/core';
import { useTable } from '@refinedev/antd';
import {
  Button,
  Empty,
  Flex,
  Pagination,
  Segmented,
  Spin,
  Typography,
  theme,
} from 'antd';
import dayjs from 'dayjs';
import type { ActivityOut } from '@/api/generated/schemas';
import { activityGroupVerbs, type ActivityGroup } from './publicationActivity';
import { PublicationActivityRow } from './PublicationActivityRow';

const { Text } = Typography;

const T = 'user_catalog.publications.activity';

type GroupFilter = 'all' | ActivityGroup;

interface PublicationActivityFeedProps {
  publicationId: number;
}

/**
 * Audit trail of one publication as a day-grouped feed, paged on the server.
 * Every publication event is written with the publication as its subject —
 * none targets it — so the subject filter alone catches the whole trail.
 * Days group within a page, so one day can continue onto the next.
 */
export const PublicationActivityFeed = ({
  publicationId,
}: PublicationActivityFeedProps) => {
  const t = useTranslate();
  const locale = useGetLocale()();
  const { token } = theme.useToken();
  const [group, setGroup] = useState<GroupFilter>('all');
  const [expanded, setExpanded] = useState<Set<number>>(new Set());

  const {
    result,
    tableQuery,
    setFilters,
    currentPage,
    setCurrentPage,
    pageSize,
  } = useTable<ActivityOut>({
    resource: 'system.activities',
    filters: {
      permanent: [
        { field: 'object_type', operator: 'eq', value: 'Publication' },
        { field: 'object_id', operator: 'eq', value: publicationId },
      ],
    },
    sorters: { initial: [{ field: 'created_at', order: 'desc' }] },
    pagination: { pageSize: 20 },
    syncWithLocation: false,
  });
  const activities = result?.data ?? [];

  const changeGroup = (value: GroupFilter) => {
    setGroup(value);
    setFilters(
      [
        {
          field: 'verb',
          operator: 'in',
          value: value === 'all' ? undefined : activityGroupVerbs(value),
        },
      ],
      'merge'
    );
  };

  const allExpanded =
    activities.length > 0 && activities.every((a) => expanded.has(a.id));

  const toggle = (id: number) =>
    setExpanded((prev) => {
      const next = new Set(prev);
      if (!next.delete(id)) next.add(id);
      return next;
    });

  const days: { day: string; activities: ActivityOut[] }[] = [];
  for (const activity of activities) {
    const day = dayjs(activity.created_at).format('YYYY-MM-DD');
    const last = days[days.length - 1];
    if (last?.day === day) last.activities.push(activity);
    else days.push({ day, activities: [activity] });
  }

  const dayLabel = (day: string) => {
    const label = dayjs(day)
      .locale(locale ?? 'pl')
      .format('dddd, D MMMM YYYY');
    return label.charAt(0).toUpperCase() + label.slice(1);
  };

  return (
    <Flex vertical gap={token.margin}>
      <Flex gap={token.marginSM} align="center" justify="space-between" wrap>
        <Segmented<GroupFilter>
          value={group}
          onChange={changeGroup}
          options={(['all', 'data', 'moderation'] as const).map((value) => ({
            value,
            label: t(`${T}.groups.${value}`),
          }))}
        />
        <Button
          disabled={activities.length === 0}
          onClick={() =>
            setExpanded(
              allExpanded ? new Set() : new Set(activities.map((a) => a.id))
            )
          }
        >
          {t(allExpanded ? `${T}.collapse_all` : `${T}.expand_all`)}
        </Button>
      </Flex>

      {tableQuery.isLoading ? (
        <Flex justify="center" style={{ padding: token.paddingLG }}>
          <Spin />
        </Flex>
      ) : days.length === 0 ? (
        <Empty
          image={Empty.PRESENTED_IMAGE_SIMPLE}
          description={t('messages.no_data')}
        />
      ) : (
        days.map(({ day, activities: dayActivities }) => (
          <Flex key={day} vertical gap={token.marginXS}>
            <Text
              type="secondary"
              strong
              style={{ fontSize: token.fontSizeSM }}
            >
              {dayLabel(day)}
            </Text>
            <div
              style={{
                border: `1px solid ${token.colorBorderSecondary}`,
                borderBottom: 'none',
                borderRadius: token.borderRadiusLG,
                overflow: 'hidden',
              }}
            >
              {dayActivities.map((activity) => (
                <PublicationActivityRow
                  key={activity.id}
                  activity={activity}
                  expanded={expanded.has(activity.id)}
                  onToggle={() => toggle(activity.id)}
                />
              ))}
            </div>
          </Flex>
        ))
      )}

      <Flex justify="flex-end">
        <Pagination
          current={currentPage}
          pageSize={pageSize}
          total={result?.total ?? 0}
          onChange={setCurrentPage}
          showSizeChanger={false}
          hideOnSinglePage
        />
      </Flex>
    </Flex>
  );
};
